import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { get } from "../api";
import { useI18n } from "../i18n";

export interface Me {
  email: string;
  email_verified: boolean;
  workspaces: { id: string; name: string; capabilities: string[] }[];
}

export interface SidebarProps {
  me: Me;
  onLogout: () => void;
}

interface SiteItem {
  id: string;
  name: string;
  mode: string;
  status: string;
}

export function Sidebar({ me, onLogout }: SidebarProps) {
  const { t, locale, setLocale } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();

  // Extract params from pathname if available
  // Path formats: /w/:workspaceId, /w/:workspaceId/sites/:siteId, /account, /
  const pathParts = location.pathname.split("/").filter(Boolean);
  const isWorkspaceContext = pathParts[0] === "w" && Boolean(pathParts[1]);
  const activeWorkspaceId = isWorkspaceContext ? pathParts[1] : undefined;
  const isSiteContext = isWorkspaceContext && pathParts[2] === "sites" && Boolean(pathParts[3]);
  const activeSiteId = isSiteContext ? pathParts[3] : undefined;

  const [sites, setSites] = useState<SiteItem[]>([]);
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = useState(false);
  const [siteMenuOpen, setSiteMenuOpen] = useState(false);
  const [serviceStatus, setServiceStatus] = useState<"ok" | "checking">("ok");

  const wsDropdownRef = useRef<HTMLDivElement>(null);
  const siteDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wsDropdownRef.current && !wsDropdownRef.current.contains(e.target as Node)) {
        setWorkspaceMenuOpen(false);
      }
      if (siteDropdownRef.current && !siteDropdownRef.current.contains(e.target as Node)) {
        setSiteMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch sites for active workspace
  useEffect(() => {
    if (!activeWorkspaceId) {
      setSites([]);
      return;
    }
    get<{ items: SiteItem[] }>(`/api/v1/workspaces/${activeWorkspaceId}/sites`)
      .then((res) => setSites(res.items))
      .catch(() => setSites([]));
  }, [activeWorkspaceId]);

  // Current active workspace object
  const activeWorkspace = me.workspaces.find((w) => w.id === activeWorkspaceId) ??
    (me.workspaces.length > 0 ? me.workspaces[0] : null);

  // Current active site object
  const activeSite = sites.find((s) => s.id === activeSiteId) ?? null;

  // Current query params (for active tab/view)
  const searchParams = new URLSearchParams(location.search);
  const currentView = searchParams.get("view") || (searchParams.get("tab") === "sources" ? "integration" : "overview");

  function checkService() {
    setServiceStatus("checking");
    setTimeout(() => {
      setServiceStatus("ok");
    }, 600);
  }

  return (
    <aside className="sidebar">
      {/* Top Branding matching Open Codex */}
      <div className="sidebar-header">
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <div className="sidebar-logo">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <circle cx="12" cy="12" r="3" fill="#ffffff" />
            </svg>
          </div>
          <span className="sidebar-brand-name">{t.common.brand}</span>
        </Link>
        <span className="sidebar-version-badge">{t.common.version}</span>
      </div>

      {/* Context Selectors: Workspace & Site */}
      <div className="sidebar-context-box">
        {/* Workspace Switcher */}
        <div style={{ position: "relative" }} ref={wsDropdownRef}>
          <button
            type="button"
            className="context-switcher-btn"
            onClick={() => setWorkspaceMenuOpen((v) => !v)}
            title={activeWorkspace ? activeWorkspace.name : t.common.switchWorkspace}
          >
            <span className="context-switcher-icon">🏢</span>
            <span className="context-switcher-text">
              {activeWorkspace ? activeWorkspace.name : t.common.switchWorkspace}
            </span>
            <span className="context-switcher-arrow">▾</span>
          </button>

          {workspaceMenuOpen && (
            <div className="context-dropdown">
              <div className="context-dropdown-header">{t.common.workspaces}</div>
              <div className="context-dropdown-list">
                {me.workspaces.map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    className={`context-dropdown-item ${w.id === activeWorkspaceId ? "active" : ""}`}
                    onClick={() => {
                      setWorkspaceMenuOpen(false);
                      navigate(`/w/${w.id}`);
                    }}
                  >
                    <span>{w.name}</span>
                    {w.id === activeWorkspaceId && <span>✓</span>}
                  </button>
                ))}
              </div>
              <div className="context-dropdown-footer">
                <button
                  type="button"
                  className="context-dropdown-action"
                  onClick={() => {
                    setWorkspaceMenuOpen(false);
                    navigate("/");
                  }}
                >
                  + {t.common.allWorkspaces}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Site Switcher (Visible when in workspace context) */}
        {activeWorkspaceId && (
          <div style={{ position: "relative", marginTop: 6 }} ref={siteDropdownRef}>
            <button
              type="button"
              className="context-switcher-btn site-btn"
              onClick={() => setSiteMenuOpen((v) => !v)}
              title={activeSite ? activeSite.name : t.common.switchSite}
            >
              <span className="context-switcher-icon">🌐</span>
              <span className="context-switcher-text">
                {activeSite ? activeSite.name : t.common.switchSite}
              </span>
              <span className="context-switcher-arrow">▾</span>
            </button>

            {siteMenuOpen && (
              <div className="context-dropdown">
                <div className="context-dropdown-header">{t.workspaceDetail.sitesSection}</div>
                <div className="context-dropdown-list">
                  {sites.length === 0 ? (
                    <div className="context-dropdown-empty">{t.workspaceDetail.noSites}</div>
                  ) : (
                    sites.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className={`context-dropdown-item ${s.id === activeSiteId ? "active" : ""}`}
                        onClick={() => {
                          setSiteMenuOpen(false);
                          navigate(`/w/${activeWorkspaceId}/sites/${s.id}`);
                        }}
                      >
                        <span>{s.name}</span>
                        <span className="muted" style={{ fontSize: 11 }}>({s.mode})</span>
                      </button>
                    ))
                  )}
                </div>
                <div className="context-dropdown-footer">
                  <button
                    type="button"
                    className="context-dropdown-action"
                    onClick={() => {
                      setSiteMenuOpen(false);
                      navigate(`/w/${activeWorkspaceId}`);
                    }}
                  >
                    + {t.workspaceDetail.addSiteButton}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main Navigation */}
      <nav className="sidebar-nav">
        {/* If inside a Site Context: Display 8 Site Level Analytics Dimensions */}
        {isSiteContext && activeWorkspaceId && activeSiteId ? (
          <>
            <div className="sidebar-section-title">{t.nav.siteAnalytics}</div>

            <Link
              to={`/w/${activeWorkspaceId}/sites/${activeSiteId}?view=overview`}
              className={`sidebar-item ${currentView === "overview" ? "active" : ""}`}
            >
              <span className="sidebar-item-icon">📊</span>
              <span>{t.nav.overview}</span>
            </Link>

            <Link
              to={`/w/${activeWorkspaceId}/sites/${activeSiteId}?view=sources_traffic`}
              className={`sidebar-item ${currentView === "sources_traffic" ? "active" : ""}`}
            >
              <span className="sidebar-item-icon">🌐</span>
              <span>{t.nav.sources}</span>
            </Link>

            <Link
              to={`/w/${activeWorkspaceId}/sites/${activeSiteId}?view=content`}
              className={`sidebar-item ${currentView === "content" ? "active" : ""}`}
            >
              <span className="sidebar-item-icon">📄</span>
              <span>{t.nav.content}</span>
            </Link>

            <Link
              to={`/w/${activeWorkspaceId}/sites/${activeSiteId}?view=audience`}
              className={`sidebar-item ${currentView === "audience" ? "active" : ""}`}
            >
              <span className="sidebar-item-icon">👥</span>
              <span>{t.nav.audience}</span>
            </Link>

            <Link
              to={`/w/${activeWorkspaceId}/sites/${activeSiteId}?view=bots`}
              className={`sidebar-item ${currentView === "bots" ? "active" : ""}`}
            >
              <span className="sidebar-item-icon">🤖</span>
              <span>{t.nav.bots}</span>
            </Link>

            <Link
              to={`/w/${activeWorkspaceId}/sites/${activeSiteId}?view=vitals`}
              className={`sidebar-item ${currentView === "vitals" ? "active" : ""}`}
            >
              <span className="sidebar-item-icon">⚡</span>
              <span>{t.nav.vitals}</span>
            </Link>

            <Link
              to={`/w/${activeWorkspaceId}/sites/${activeSiteId}?view=integration`}
              className={`sidebar-item ${currentView === "integration" ? "active" : ""}`}
            >
              <span className="sidebar-item-icon">🔌</span>
              <span>{t.nav.integration}</span>
            </Link>

            <Link
              to={`/w/${activeWorkspaceId}/sites/${activeSiteId}?view=settings`}
              className={`sidebar-item ${currentView === "settings" ? "active" : ""}`}
            >
              <span className="sidebar-item-icon">⚙️</span>
              <span>{t.nav.settings}</span>
            </Link>

            <div className="sidebar-section-title" style={{ marginTop: 8 }}>
              {t.nav.workspaceManagement}
            </div>

            <Link
              to={`/w/${activeWorkspaceId}`}
              className="sidebar-item"
            >
              <span className="sidebar-item-icon">🏢</span>
              <span>{t.nav.quotaUsage}</span>
            </Link>

            <Link
              to={`/w/${activeWorkspaceId}`}
              className="sidebar-item"
            >
              <span className="sidebar-item-icon">👥</span>
              <span>{t.nav.members}</span>
            </Link>
          </>
        ) : isWorkspaceContext && activeWorkspaceId ? (
          /* Workspace Level Navigation */
          <>
            <div className="sidebar-section-title">{t.nav.workspaceManagement}</div>

            <Link
              to={`/w/${activeWorkspaceId}`}
              className={`sidebar-item ${location.pathname === `/w/${activeWorkspaceId}` && !location.hash ? "active" : ""}`}
            >
              <span className="sidebar-item-icon">📊</span>
              <span>{t.nav.quotaUsage}</span>
            </Link>

            <Link
              to={`/w/${activeWorkspaceId}`}
              className="sidebar-item"
            >
              <span className="sidebar-item-icon">🌐</span>
              <span>{t.nav.sites} ({sites.length})</span>
            </Link>

            <Link
              to={`/w/${activeWorkspaceId}`}
              className="sidebar-item"
            >
              <span className="sidebar-item-icon">👥</span>
              <span>{t.nav.members}</span>
            </Link>
          </>
        ) : null}

        {/* Global Navigation */}
        <div className="sidebar-section-title" style={{ marginTop: 8 }}>
          {t.common.workspaces}
        </div>

        <Link
          to="/"
          className={`sidebar-item ${location.pathname === "/" ? "active" : ""}`}
        >
          <span className="sidebar-item-icon">🗂️</span>
          <span>{t.common.allWorkspaces}</span>
        </Link>

        <Link
          to="/account"
          className={`sidebar-item ${location.pathname === "/account" ? "active" : ""}`}
        >
          <span className="sidebar-item-icon">🛡️</span>
          <span>{t.common.accountSecurity}</span>
        </Link>
      </nav>

      {/* Bottom Utility Dock matching Open Codex screenshot */}
      <div className="sidebar-footer">
        {/* Language selector */}
        <div
          className="sidebar-footer-row"
          onClick={() => setLocale(locale === "zh" ? "en" : "zh")}
          title="Switch Language / 切换语言"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>🌐</span>
            <span>{locale === "zh" ? "中文 (简体)" : "English"}</span>
          </div>
          <span style={{ fontSize: 11, color: "#94a3b8" }}>›</span>
        </div>

        {/* Theme indicator */}
        <div className="sidebar-footer-row">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>💻</span>
            <span>{t.common.systemTheme}</span>
          </div>
        </div>

        {/* Service & Agent Status with refresh */}
        <div className="sidebar-footer-row" style={{ cursor: "default" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="status-dot" />
            <span style={{ color: "#15803d", fontWeight: 500 }}>
              {serviceStatus === "checking" ? t.common.loading : t.common.online}
            </span>
          </div>
          <button
            type="button"
            className="sidebar-icon-btn"
            title={t.common.refresh}
            onClick={checkService}
          >
            🔄
          </button>
        </div>

        {/* User Account & Logout */}
        <div className="sidebar-user-row">
          <div className="sidebar-user-info" title={me.email}>
            <span className="sidebar-avatar">{me.email[0].toUpperCase()}</span>
            <span className="sidebar-user-email">{me.email}</span>
          </div>
          <button
            type="button"
            className="sidebar-icon-btn"
            onClick={onLogout}
            title={t.common.logout}
          >
            🚪
          </button>
        </div>
      </div>
    </aside>
  );
}
